import React from 'react';
import {
  AlignLeft,
  Grid2x2Plus,
  Calendar,
  ChevronDown,
  CircleCheck,
  SquareCheck,
  Type,
} from 'lucide-react';
import { HTS_ANSWER_CLASSIFICATION_LABELS, HtsAnswerClassification, HtsInputType } from '@evaltick/shared';
import { HTS_INPUT_TYPE_LABELS } from '@/utils/htsInputTypeLabels';
import { HtsQuestionBehavior } from './htsQuestionBehavior';

export interface AnswerTypeCardConfig {
  value: HtsInputType;
  label: string;
  hint: string;
  icon: React.ReactNode;
}

export const ANSWER_TYPE_CARDS: AnswerTypeCardConfig[] = [
  {
    value: HtsInputType.TEXT,
    label: HTS_INPUT_TYPE_LABELS[HtsInputType.TEXT],
    hint: 'Single-line answer.',
    icon: <Type style={{ width: '1em', height: '1em' }} />,
  },
  {
    value: HtsInputType.TEXTAREA,
    label: HTS_INPUT_TYPE_LABELS[HtsInputType.TEXTAREA],
    hint: 'Multi-line, detailed answer.',
    icon: <AlignLeft style={{ width: '1em', height: '1em' }} />,
  },
  {
    value: HtsInputType.DATE,
    label: HTS_INPUT_TYPE_LABELS[HtsInputType.DATE],
    hint: 'Candidate picks a calendar date.',
    icon: <Calendar style={{ width: '1em', height: '1em' }} />,
  },
  {
    value: HtsInputType.SELECT,
    label: HTS_INPUT_TYPE_LABELS[HtsInputType.SELECT],
    hint: 'One choice from a list you define.',
    icon: <ChevronDown style={{ width: '1em', height: '1em' }} />,
  },
  {
    value: HtsInputType.CHECKBOXES,
    label: HTS_INPUT_TYPE_LABELS[HtsInputType.CHECKBOXES],
    hint: 'Multiple choices from a list you define.',
    icon: <SquareCheck style={{ width: '1em', height: '1em' }} />,
  },
  {
    value: HtsInputType.YES_NO,
    label: HTS_INPUT_TYPE_LABELS[HtsInputType.YES_NO],
    hint: 'Binary — used in eligibility gates.',
    icon: <CircleCheck style={{ width: '1em', height: '1em' }} />,
  },
  {
    value: HtsInputType.REPEATABLE_GROUP,
    label: 'Repeatable list',
    hint: 'Add/remove rows, e.g. employment history.',
    icon: <Grid2x2Plus style={{ width: '1em', height: '1em' }} />,
  },
];

export type BehaviorTint = 'periwinkle' | 'orange' | 'red' | 'purple' | 'green';

export interface BehaviorCardConfig {
  value: HtsQuestionBehavior;
  label: string;
  sub?: string;
  hint: string;
  detail: string;
  letter: string;
  tint: BehaviorTint;
}

export const BEHAVIOR_CARDS: BehaviorCardConfig[] = [
  {
    value: HtsQuestionBehavior.RecordOnly,
    label: 'Just record the answer',
    hint: 'No follow-up, no flag — the answer is simply saved.',
    detail:
      'Use for background information you want on file. It never triggers a flag or AI review, but can optionally feed a Readiness pillar.',
    letter: 'R',
    tint: 'periwinkle',
  },
  {
    value: HtsQuestionBehavior.HardGate,
    label: 'Flag on a specific answer',
    sub: '(eligibility gate)',
    hint: 'Flags the evaluator immediately on a match — no AI involved.',
    detail:
      'Rule-based only: pick the exact answer that should trip the flag. The evaluator sees it immediately, with the priority and reason you set.',
    letter: 'G',
    tint: 'orange',
  },
  {
    value: HtsQuestionBehavior.Sensitive,
    label: 'Sensitive — straight to evaluator',
    sub: '(health / mental health)',
    hint: 'Sent verbatim to the evaluator — never AI-reviewed or followed up.',
    detail:
      'Deliberately terminal: no AI classification, no pillar scoring, no automatic follow-up. Use for health disclosures and similarly sensitive topics.',
    letter: 'S',
    tint: 'red',
  },
  {
    value: HtsQuestionBehavior.Suitability,
    label: 'Character & Background — AI review',
    hint: `AI classifies ${HTS_ANSWER_CLASSIFICATION_LABELS[HtsAnswerClassification.SUITABLE]} → ${HTS_ANSWER_CLASSIFICATION_LABELS[HtsAnswerClassification.UNSUITABLE]} — never shown as a flag.`,
    detail:
      'Scored by AI against a classification topic you pick. Always feeds the holistic Integrity pillar, not an individually tagged one.',
    letter: 'C',
    tint: 'purple',
  },
  {
    value: HtsQuestionBehavior.Readiness,
    label: 'Readiness Profile — AI follow-up',
    hint: 'Feeds a pillar; AI follows up only if vague; never flags.',
    detail:
      'Pick which Readiness pillar this scores. If the answer is too vague the AI asks a natural follow-up before scoring — the candidate never sees a flag.',
    letter: 'P',
    tint: 'green',
  },
];

export const BEHAVIOR_CARD_BY_VALUE: Record<HtsQuestionBehavior, BehaviorCardConfig> = BEHAVIOR_CARDS.reduce(
  (acc, card) => {
    acc[card.value] = card;
    return acc;
  },
  {} as Record<HtsQuestionBehavior, BehaviorCardConfig>
);

export interface WizardStepConfig {
  key: string;
  label: string;
}

export const WIZARD_STEPS: WizardStepConfig[] = [
  { key: 'content', label: 'Content' },
  { key: 'section', label: 'Section' },
  { key: 'type', label: 'Type' },
  { key: 'behavior', label: 'Behavior' },
  { key: 'configure', label: 'Configure' },
  { key: 'review', label: 'Review' },
];
